import React from 'react';
import { motion } from 'framer-motion';
import IndustryPageHeading from '../components/IndustryPageHeading';
import EnterpriseVideo from '../components/EnterpriseVideo';

/**
 * Real Estate Solutions — internal route /solutions/industries/real-estate
 *
 * Three content sections with alternating layout direction (text/video),
 * mirroring the first three sections of the Logistics Solutions page and
 * matching the Insurance industry subpage. Each video slot is an empty
 * champagne-gold-bordered placeholder frame labeled "Video Demo Coming
 * Soon", alternating sides (video right, then left, then right). After
 * the three sections the page goes directly to the site's standard
 * footer, which is rendered globally by Layout.
 */

const VideoDemoFrame = ({ cloudflareVideoId }) => {
  if (!cloudflareVideoId) {
    return (
      <div className="w-full">
        <div
          className="relative w-full overflow-hidden rounded-lg bg-gradient-to-br from-[#E8E4DC] to-[#E8EDE7]"
          style={{
            aspectRatio: '16 / 9',
            border: '2px solid #D4AF37',
            boxShadow:
              'inset 0 2px 8px rgba(20, 30, 48, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)',
          }}
          data-testid="real-estate-video-placeholder"
        >
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="text-base sm:text-lg text-black/60 font-medium tracking-wide">
              Video Demo Coming Soon
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <EnterpriseVideo
        cloudflareVideoId={cloudflareVideoId}
        title="Real estate demo video"
        testId={`real-estate-video-${cloudflareVideoId}`}
        className="w-full rounded-lg bg-gradient-to-br from-[#E8E4DC] to-[#E8EDE7]"
        style={{
          aspectRatio: '16 / 9',
          border: '2px solid #D4AF37',
          boxShadow:
            'inset 0 2px 8px rgba(20, 30, 48, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)',
        }}
      />
    </div>
  );
};

const FeatureBlock = ({ title, description }) => (
  <div className="pl-4 relative">
    <div
      className="absolute left-0 top-1 bottom-1 w-0.5 rounded-full"
      style={{
        background: 'linear-gradient(to bottom, #D4AF37, rgba(212, 175, 55, 0.4))',
      }}
    />
    <h3 className="text-base sm:text-lg font-medium text-black/90">{title}</h3>
    <p className="text-sm text-black/70 mt-1">{description}</p>
  </div>
);

const TextContent = ({ eyebrow, heading, body, features, isPageHeading }) => {
  const HeadingTag = isPageHeading ? 'h1' : 'h2';
  return (
    <div>
      <p className="text-sm md:text-base font-bold text-black/90 mb-3 md:mb-4 uppercase tracking-wider">
        {eyebrow}
      </p>
      <HeadingTag className="text-3xl md:text-4xl font-medium text-black/90 mb-4 md:mb-6">
        {heading}
      </HeadingTag>
      <p className="text-base sm:text-lg text-black/70 mb-8 md:mb-10">{body}</p>
      <div className="space-y-6">
        {features.map((f) => (
          <FeatureBlock key={f.title} title={f.title} description={f.description} />
        ))}
      </div>
    </div>
  );
};

const sections = [
  {
    eyebrow: 'Lead Qualification & Matching',
    cloudflareVideoId: 'c9cc78e58a666498d6813737cf48e127',
    heading: 'Every lead, qualified before an agent picks up the phone.',
    body:
      'GoogLyn reads incoming inquiries, scores buyer or renter intent, and matches them against active listings \u2014 so agents spend time closing, not chasing.',
    features: [
      {
        title: 'Automated lead scoring',
        description: 'Inquiries ranked by intent and budget the moment they arrive.',
      },
      {
        title: 'Instant property matching',
        description: 'Listings recommended based on stated and inferred preferences.',
      },
      {
        title: 'Smart showing scheduling',
        description: 'Calendar coordination handled without back-and-forth emails.',
      },
    ],
  },
  {
    eyebrow: 'Transaction Management',
    cloudflareVideoId: '95a97a75dfd93d315bef4bf1edd42e7e',
    heading: 'From offer to closing, without the paperwork chase.',
    body:
      'GoogLyn tracks every document, deadline, and signature across a transaction \u2014 keeping agents, buyers, sellers, and lenders aligned without a single missed step.',
    features: [
      {
        title: 'Automated document processing',
        description: 'Contracts and disclosures routed and tracked automatically.',
      },
      {
        title: 'Deadline & milestone tracking',
        description: 'Every contingency date monitored in real time.',
      },
      {
        title: 'Auto-updates to all parties',
        description: 'Buyers, sellers, and agents stay informed without status-check calls.',
      },
    ],
  },
  {
    eyebrow: 'Portfolio & Tenant Operations',
    cloudflareVideoId: '29c37a09a73c7b985607abaef6e88842',
    heading: 'One property tracker, total visibility.',
    body:
      'Give owners, property managers, and tenants a single source of truth for maintenance, rent, and performance \u2014 from move-in to renewal.',
    features: [
      {
        title: '24/7 tenant support',
        description: 'Maintenance requests and questions handled around the clock.',
      },
      {
        title: 'Automated rent & reconciliation',
        description: 'Collection and reconciliation run without manual follow-up.',
      },
      {
        title: 'Real-time portfolio reporting',
        description: 'Occupancy and performance data always current.',
      },
    ],
  },
];

const IndustryRealEstate = () => {
  return (
    <div className="w-full" data-testid="real-estate-solutions-page">
      <IndustryPageHeading name="Real Estate" testId="real-estate-page-heading" />
      {sections.map((section, idx) => {
        const textFirst = idx % 2 === 0; // sections 1,3 → text left (video right); 2 → video left
        const bg = idx % 2 === 0 ? 'bg-white' : 'bg-[#F5F7F5]';
        return (
          <section
            key={section.eyebrow}
            className={`py-16 md:py-24 ${bg}`}
            data-testid={`real-estate-section-${idx + 1}`}
          >
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center"
              >
                {textFirst ? (
                  <>
                    <TextContent {...section} isPageHeading={idx === 0} />
                    <VideoDemoFrame cloudflareVideoId={section.cloudflareVideoId} />
                  </>
                ) : (
                  <>
                    {/* Video first on desktop; text stays above video on mobile for readability */}
                    <div className="order-2 lg:order-1">
                      <VideoDemoFrame cloudflareVideoId={section.cloudflareVideoId} />
                    </div>
                    <div className="order-1 lg:order-2">
                      <TextContent {...section} isPageHeading={idx === 0} />
                    </div>
                  </>
                )}
              </motion.div>
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default IndustryRealEstate;
